import { ChatComposer } from '@/components/ChatComposer';
import { ChatThread } from '@/components/ChatThread';
import { StateMessage } from '@/components/StateMessage';
import type { AgentChat } from '@/features/agents/useAgentChat';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { Box, Button, CircularProgress, Stack } from '@mui/joy';

const HINT = 'Enter — надіслати · відповіді лише в межах ваших організацій';

type PanelBodyProps = {
  chat: AgentChat;
  /** The app's name, for the texts; none outside an app. */
  appLabel?: string;
  agentsCount: number;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
};

/** The panel below its header: the conversation and the composer, or why there is none. */
export const PanelBody = ({
  chat,
  appLabel,
  agentsCount,
  isLoading,
  isError,
  onRetry,
}: PanelBodyProps) => {
  if (isLoading) {
    return (
      <Stack alignItems='center' justifyContent='center' sx={{ flex: 1 }}>
        <CircularProgress size='sm' />
      </Stack>
    );
  }

  if (isError) {
    return (
      <StateMessage
        icon={<ErrorOutlineIcon />}
        color='danger'
        title='Не вдалося завантажити агентів'
        text='Сервіс Gasbi AI не відповів. Спробуйте ще раз за мить.'
      >
        <Button onClick={onRetry}>Спробувати ще раз</Button>
      </StateMessage>
    );
  }

  // ADR-0004 §2.8: an app whose panel no agent is offered in has nothing to chat with.
  if (agentsCount === 0) {
    return (
      <StateMessage
        icon={<SmartToyOutlinedIcon />}
        color='neutral'
        title={
          appLabel
            ? `Немає доступних агентів у ${appLabel}`
            : 'Немає доступних агентів'
        }
        text='Адміністратор ще не відкрив жодного агента для цього додатка. Зверніться до адміністратора Gasbi.'
      />
    );
  }

  return (
    <>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <ChatThread chat={chat} />
      </Box>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <ChatComposer
          chat={chat}
          placeholder={
            appLabel ? `Запитайте про дані ${appLabel}…` : 'Запитайте агента…'
          }
          hint={HINT}
        />
      </Box>
    </>
  );
};
